import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient, useLanguage } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { Skeleton } from "../components/ui/skeleton";
import PageHeader from "../components/layout/PageHeader";
import ProgressRing from "../components/shared/ProgressRing";
import IslamicLoadingScreen from "../components/shared/IslamicLoadingScreen";
import { SURAHS } from "../services/quranService";
import { toast } from "sonner";
import {
  Plus,
  Brain,
  CheckCircle2,
  Clock,
  Bell,
  Trash2,
  BookOpen,
  RotateCcw,
  ChevronDown,
  Check,
  Sparkles,
  Target,
  Trophy,
  List,
  Flame
} from "lucide-react";

const Hafalan = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const [memorizations, setMemorizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMem, setShowAddMem] = useState(false);
  const [selectedSurah, setSelectedSurah] = useState("");
  const [surahOpen, setSurahOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmAgree, setConfirmAgree] = useState(false);
  const memLoadRef = useRef({ inFlight: false, lastAt: 0 });
  const MEMO_CACHE_KEY = "quran-memorization-cache";
  const SRS_STORE_KEY = "quran-srs-store";
  const readSrsStore = () => {
    try {
      const raw = localStorage.getItem(SRS_STORE_KEY) || "{}";
      const obj = JSON.parse(raw);
      if (obj && typeof obj === "object") return obj;
      return {};
    } catch {
      return {};
    }
  };
  const countDueForSurah = (surahNumber, ayahs) => {
    const store = readSrsStore();
    const list = Array.isArray(ayahs) ? ayahs : [];
    let c = 0;
    for (const a of list) {
      const k = `${surahNumber}:${a}`;
      const e = store[k];
      if (!e || Date.now() >= (e.dueAt || 0)) c += 1;
    }
    return c;
  };
  const getEarliestDueAtForSurah = (surahNumber, ayahs) => {
    const store = readSrsStore();
    const list = Array.isArray(ayahs) ? ayahs : [];
    let earliest = Infinity;
    for (const a of list) {
      const k = `${surahNumber}:${a}`;
      const e = store[k];
      const due = e?.dueAt || Date.now();
      if (due < earliest) earliest = due;
    }
    return earliest === Infinity ? Date.now() + 6 * 60 * 60 * 1000 : earliest;
  };
  const toTimeString = (ts) => {
    const d = new Date(ts);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };
  const createMurajaahReminder = async (mem) => {
    try {
      const dueCount = countDueForSurah(mem.surah_number, mem.memorized_ayahs);
      const dueAt = getEarliestDueAtForSurah(mem.surah_number, mem.memorized_ayahs);
      const reminderTime = toTimeString(dueAt);
      const payload = {
        habit_id: null,
        title: isEnglish ? `Muraja'ah ${mem.surah_name}` : `Muraja'ah ${mem.surah_name}`,
        message: isEnglish
          ? `Review ${dueCount} due ayah(s)`
          : `Muraja'ah ${dueCount} ayat yang jatuh tempo`,
        reminder_time: reminderTime,
        frequency: "daily",
        sound: "default",
        days: [],
        prayer_time_anchor: "none",
        prayer_time_offset: 0,
      };
      await apiClient.post("/reminders", payload);
      toast.success(isEnglish ? "Muraja'ah reminder created" : "Pengingat muraja'ah dibuat");
    } catch (error) {
      toast.error(isEnglish ? "Failed to create reminder" : "Gagal membuat pengingat");
    }
  };

  useEffect(() => {
    const now = Date.now();
    if (!memLoadRef.current.inFlight && now - memLoadRef.current.lastAt > 5000) {
      memLoadRef.current.inFlight = true;
      memLoadRef.current.lastAt = now;
      loadData();
    }
    const failSafe = setTimeout(() => {
      setLoading(false);
    }, 15000);
    return () => clearTimeout(failSafe);
  }, []);

  const loadData = async () => {
    try {
      const memRes = await Promise.race([
        apiClient.get("/memorization"),
        new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 10000)),
      ]);
      setMemorizations(memRes.data);
    } catch (error) {
      try {
        const cached = JSON.parse(localStorage.getItem(MEMO_CACHE_KEY) || "[]");
        if (Array.isArray(cached) && cached.length) {
          setMemorizations(cached);
        }
      } catch {}
    } finally {
      memLoadRef.current.inFlight = false;
      setLoading(false);
    }
  };

  const addMemorization = async () => {
    if (!selectedSurah) return;
    
    const surah = SURAHS.find(s => s.number === parseInt(selectedSurah));
    if (!surah) return;

    try {
      const res = await apiClient.post("/memorization", {
        surah_number: surah.number,
        surah_name: surah.englishName,
        total_ayahs: surah.numberOfAyahs,
      });
      setMemorizations([...memorizations, res.data]);
      try {
        const cached = JSON.parse(localStorage.getItem(MEMO_CACHE_KEY) || "[]");
        const next = [...cached.filter(m => m.surah_number !== res.data.surah_number), res.data];
        localStorage.setItem(MEMO_CACHE_KEY, JSON.stringify(next));
      } catch {}
      setShowAddMem(false);
      setSelectedSurah("");
      toast.success(isEnglish ? "Surah added to memorization list" : "Surah ditambahkan ke daftar hafalan");
    } catch (error) {
      toast.error(error.response?.data?.detail || (isEnglish ? "Failed to add" : "Gagal menambahkan"));
    }
  };

  const deleteMemorization = async (memId) => {
    try {
      await apiClient.delete(`/memorization/${memId}`);
      setMemorizations(memorizations.filter(m => m.mem_id !== memId));
      try {
        const cached = JSON.parse(localStorage.getItem(MEMO_CACHE_KEY) || "[]");
        const next = cached.filter(m => m.mem_id !== memId);
        localStorage.setItem(MEMO_CACHE_KEY, JSON.stringify(next));
      } catch {}
      toast.success(isEnglish ? "Removed from list" : "Dihapus dari daftar");
    } catch (error) {
      toast.error(isEnglish ? "Failed to remove" : "Gagal menghapus");
    }
  };

  if (loading) {
    return (
      <IslamicLoadingScreen
        title={isEnglish ? "Loading Memorization" : "Memuat Hafalan"}
        message={isEnglish ? "Hifz requires patience and consistency..." : "Hafal butuh kesabaran dan konsistensi..."}
        language={isEnglish ? "en" : "id"}
        size="large"
      />
    );
  }

  const totalMemorized = memorizations.reduce((acc, m) => acc + (m.memorized_ayahs?.length || 0), 0);
  const totalAyahs = memorizations.reduce((acc, m) => acc + m.total_ayahs, 0);
  const overallProgress = totalAyahs > 0 ? (totalMemorized / totalAyahs) * 100 : 0;

  const availableSurahs = SURAHS.filter(
    s => !memorizations.some(m => m.surah_number === s.number)
  );
  const selectedSurahInfo = SURAHS.find((surah) => surah.number === parseInt(selectedSurah));
  const memorizationView = memorizations.map((mem) => {
    const surahMeta = SURAHS.find((s) => s.number === mem.surah_number);
    const memorizedCount = mem.memorized_ayahs?.length || 0;
    const totalAyahsCount = mem.total_ayahs || 0;
    const progress = totalAyahsCount > 0 ? (memorizedCount / totalAyahsCount) * 100 : 0;
    const isCompleted = totalAyahsCount > 0 && memorizedCount >= totalAyahsCount;
    const memorizedSet = new Set(mem.memorized_ayahs || []);
    const dueReviews = countDueForSurah(mem.surah_number, mem.memorized_ayahs);
    let nextAyah = 1;
    while (nextAyah <= totalAyahsCount && memorizedSet.has(nextAyah)) {
      nextAyah += 1;
    }
    if (nextAyah > totalAyahsCount) {
      nextAyah = null;
    }
    const surahDisplayName = surahMeta?.englishName || mem.surah_name;
    const surahArabicName = surahMeta?.name || "";
    const searchText = `${mem.surah_number} ${mem.surah_name} ${surahDisplayName} ${surahArabicName}`.toLowerCase();
    return {
      ...mem,
      memorizedCount,
      totalAyahsCount,
      progress,
      isCompleted,
      dueReviews,
      nextAyah,
      surahDisplayName,
      surahArabicName,
      searchText,
    };
  });
  const filteredMemorizations = memorizationView.filter((mem) => {
    const matchesSearch = mem.searchText.includes(searchQuery.trim().toLowerCase());
    if (!matchesSearch) return false;
    if (activeTab === "completed") return mem.isCompleted;
    if (activeTab === "in-progress") return !mem.isCompleted;
    return true;
  });

  const getTabBadge = (tabValue) => {
    if (tabValue === "all") return memorizations.length;
    if (tabValue === "in-progress") return memorizations.filter(m => {
      const mem = memorizationView.find(v => v.mem_id === m.mem_id);
      return mem && !mem.isCompleted;
    }).length;
    if (tabValue === "completed") return memorizations.filter(m => {
      const mem = memorizationView.find(v => v.mem_id === m.mem_id);
      return mem && mem.isCompleted;
    }).length;
    return 0;
  };

  return (
    <div className="min-h-screen" data-testid="hafalan-page">
      <PageHeader title={isEnglish ? "Quran Memorization" : "Hafalan Al-Qur'an"} />

      <div className="page-container space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2 md:p-4 text-center">
              <BookOpen className="w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 md:mb-2 text-primary" />
              <p className="text-lg md:text-2xl font-bold">{memorizations.length}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Total Surahs" : "Total Surah"}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2 md:p-4 text-center">
              <Target className="w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 md:mb-2 text-primary" />
              <p className="text-lg md:text-2xl font-bold">{totalMemorized}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Ayahs Memorized" : "Ayat Dihafal"}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2 md:p-4 text-center">
              <Trophy className="w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 md:mb-2 text-primary" />
              <p className="text-lg md:text-2xl font-bold">{Math.round(overallProgress)}%</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Completion" : "Penyelesaian"}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2 md:p-4 text-center">
              <Flame className="w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 md:mb-2 text-primary" />
              <p className="text-lg md:text-2xl font-bold">{memorizations.filter(m => {
                const mem = memorizationView.find(v => v.mem_id === m.mem_id);
                return mem && !mem.isCompleted;
              }).length}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "In Progress" : "Berjalan"}</p>
            </CardContent>
          </Card>
        </div>


        {/* Progress Overview with Add Button */}
        <Card className="islamic-gradient text-white">
          <CardContent className="p-3 md:p-6">
            <div className="flex items-center justify-between gap-3 md:gap-6">
              <div className="flex items-center gap-3 md:gap-6">
                <ProgressRing
                  progress={overallProgress}
                  size={70}
                  strokeWidth={6}
                  color="white"
                />
                <div>
                  <h3 className="text-lg md:text-2xl font-bold">{memorizations.length} {isEnglish ? "Surahs" : "Surah"}</h3>
                  <p className="text-xs md:text-sm text-white/80">{isEnglish ? "in memorization list" : "dalam daftar hafalan"}</p>
                  <p className="text-[10px] md:text-sm text-white/70 mt-1">
                    {totalMemorized} / {totalAyahs} {isEnglish ? "verses memorized" : "ayat dihafal"}
                  </p>
                </div>
              </div>
              <Dialog open={showAddMem} onOpenChange={setShowAddMem}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-white text-primary hover:bg-white/90 shadow-lg text-xs md:text-sm" data-testid="add-memorization-btn">
                    <Plus className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" /> {isEnglish ? "Add Surah" : "Tambah Surah"}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{isEnglish ? "Add Surah to Memorize" : "Tambah Surah untuk Dihafal"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-3">
                      <Label>{isEnglish ? "Select Surah" : "Pilih Surah"}</Label>
                      <Popover open={surahOpen} onOpenChange={setSurahOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={surahOpen}
                            className="w-full h-11 justify-between"
                          >
                            {selectedSurahInfo
                              ? `${selectedSurahInfo.number}. ${selectedSurahInfo.englishName} (${selectedSurahInfo.numberOfAyahs} ${isEnglish ? "verses" : "ayat"})`
                              : isEnglish ? "Select surah..." : "Pilih surah..."}
                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                          <Command className="rounded-md [&_[cmdk-input-wrapper]]:mx-3 [&_[cmdk-input-wrapper]]:mt-3 [&_[cmdk-input-wrapper]]:mb-2 [&_[cmdk-input-wrapper]]:rounded-md [&_[cmdk-input-wrapper]]:border [&_[cmdk-input-wrapper]]:border-input [&_[cmdk-input-wrapper]]:px-3 [&_[cmdk-input-wrapper]]:py-1 [&_[cmdk-input-wrapper]]:shadow-sm [&_[cmdk-input]]:h-9 [&_[cmdk-input]]:focus-visible:outline-none [&_[cmdk-input]]:focus-visible:ring-0">
                            <CommandInput placeholder={isEnglish ? "Search surah..." : "Cari surah..."} />
                            <CommandEmpty>{isEnglish ? "Surah not found." : "Surah tidak ditemukan."}</CommandEmpty>
                            <CommandList className="max-h-60">
                              <CommandGroup>
                                {availableSurahs.map((surah) => (
                                  <CommandItem
                                    key={surah.number}
                                    value={`${surah.number}. ${surah.englishName} ${surah.name}`}
                                    onSelect={() => {
                                      setSelectedSurah(surah.number.toString());
                                      setSurahOpen(false);
                                    }}
                                  >
                                    <span className="flex-1">
                                      {surah.number}. {surah.englishName} ({surah.numberOfAyahs} {isEnglish ? "verses" : "ayat"})
                                    </span>
                                    {selectedSurah === surah.number.toString() && (
                                      <Check className="h-4 w-4 text-primary" />
                                    )}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={addMemorization} disabled={!selectedSurah}>
                      {isEnglish ? "Add" : "Tambahkan"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="relative">
          <Brain className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={isEnglish ? "Search memorization..." : "Cari hafalan..."}
            className="pl-10 md:pl-12 h-11 md:h-14 text-sm md:text-base border-2"
          />
        </div>

        {/* Premium Tabs */}
        <Card className="overflow-hidden">
          {/* Premium Tab Header */}
          <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background p-1">
            <div className="flex gap-1">
              {[
                { value: "all", label: isEnglish ? "Semua" : "Semua", icon: List, badge: getTabBadge("all") },
                { value: "in-progress", label: isEnglish ? "Berjalan" : "Berjalan", icon: Clock, badge: getTabBadge("in-progress") },
                { value: "completed", label: isEnglish ? "Selesai" : "Selesai", icon: CheckCircle2, badge: getTabBadge("completed") }
              ].map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.value;

                return (
                  <button
                    key={tab.value}
                    onClick={() => setActiveTab(tab.value)}
                    className={`flex-1 relative flex items-center justify-center gap-1 md:gap-2 px-2 md:px-3 py-2 md:py-3 rounded-xl font-medium text-xs md:text-sm transition-all duration-300 ${
                      isActive
                        ? "bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg shadow-primary/25"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                    }`}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/50 to-transparent rounded-xl animate-pulse" />
                    )}
                    <Icon className={`w-4 h-4 ${isActive ? "text-white" : ""}`} />
                    <span className="relative">{tab.label}</span>
                    {tab.badge !== null && tab.badge > 0 && (
                      <span
                        className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          isActive
                            ? "bg-white/20 text-white border-white/30"
                            : "bg-primary/10 text-primary border-primary/20"
                        }`}
                      >
                        {tab.badge}
                      </span>
                    )}
                    {isActive && (
                      <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400 animate-pulse" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <CardContent className="p-3 md:p-6">
            {/* Memorization List */}
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
              </div>
            ) : memorizations.length === 0 ? (
              <Card>
                <CardContent className="p-6 md:p-12 text-center text-muted-foreground">
                  <Brain className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 opacity-50" />
                  <p className="text-sm md:text-base font-medium">{isEnglish ? "No surah in memorization list yet" : "Belum ada surah dalam daftar hafalan"}</p>
                  <p className="text-xs md:text-sm mt-2">{isEnglish ? "Click 'Add Surah' to start memorizing" : "Klik 'Tambah Surah' untuk mulai menghafal"}</p>
                </CardContent>
              </Card>
            ) : filteredMemorizations.length === 0 ? (
              <Card>
                <CardContent className="p-6 md:p-12 text-center text-muted-foreground">
                  <Brain className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 opacity-50" />
                  <p className="text-sm md:text-base font-medium">{isEnglish ? "No memorization matches your search" : "Tidak ada hafalan yang cocok"}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2 md:space-y-3">
                {filteredMemorizations.map((mem) => {
                  return (
                    <Card
                      key={mem.mem_id}
                      data-testid={`mem-card-${mem.surah_number}`}
                      className={`cursor-pointer hover:shadow-lg hover:scale-[1.01] transition-all duration-300 group ${
                        mem.isCompleted ? "border-primary/50 bg-gradient-to-r from-primary/5 to-transparent" : ""
                      }`}
                    >
                      <CardContent className="p-3 md:p-5">
                        <div className="flex items-start gap-2 md:gap-4">
                          <div className={`w-10 h-10 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 flex-shrink-0 ${
                            mem.isCompleted
                              ? "bg-gradient-to-br from-emerald-500 to-teal-600 text-white"
                              : "bg-gradient-to-br from-primary to-primary/80 text-white"
                          }`}>
                            {mem.isCompleted ? <CheckCircle2 className="w-5 h-5 md:w-8 md:h-8" /> : <span className="text-sm md:text-xl font-bold">{mem.surah_number}</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 md:gap-2 mb-1">
                              <h3 className="font-bold text-sm md:text-lg truncate group-hover:text-primary transition-colors">{mem.surah_name}</h3>
                              {mem.isCompleted && (
                                <CheckCircle2 className="w-3 h-3 md:w-5 md:h-5 text-emerald-500 flex-shrink-0" />
                              )}
                            </div>
                            {mem.dueReviews > 0 && (
                              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] md:text-xs">
                                <Clock className="w-3 h-3" />
                                {isEnglish ? `${mem.dueReviews} due` : `${mem.dueReviews} jatuh tempo`}
                              </div>
                            )}
                            <p className="text-[10px] md:text-sm text-muted-foreground">
                              {mem.memorizedCount} / {mem.totalAyahsCount} {isEnglish ? "verses" : "ayat"}
                            </p>
                            <Progress value={mem.progress} className="h-1.5 md:h-2.5 mt-1 md:mt-2" />
                            <div className="flex items-center gap-1 md:gap-2 mt-2 md:mt-3 flex-wrap">
                              {!mem.isCompleted && mem.nextAyah && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-[10px] md:text-xs h-7 md:h-8"
                                  onClick={() => navigate(`/quran/${mem.surah_number}?ayah=${mem.nextAyah}`)}
                                >
                                  <RotateCcw className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" />
                                  {isEnglish ? `Continue ${mem.nextAyah}` : `Lanjut ${mem.nextAyah}`}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-[10px] md:text-xs h-7 md:h-8"
                                onClick={() => navigate(`/quran/${mem.surah_number}`)}
                              >
                                <BookOpen className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" />
                                {isEnglish ? "Read" : "Baca"}
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                className="text-[10px] md:text-xs h-7 md:h-8 bg-primary/10 hover:bg-primary/20 text-primary"
                                onClick={() => navigate(`/quiz/play?category=surah-hafal&surah=${mem.surah_number}`)}
                              >
                                <Brain className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" />
                                {isEnglish ? "Quiz" : "Quiz"}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-[10px] md:text-xs h-7 md:h-8"
                                onClick={() => createMurajaahReminder(mem)}
                              >
                                <Bell className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" />
                                {isEnglish ? "Muraja'ah Reminder" : "Pengingat Muraja'ah"}
                              </Button>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 md:gap-2 flex-shrink-0">
                            <div className="text-right">
                              <p className={`text-lg md:text-2xl font-bold ${mem.isCompleted ? "text-emerald-600 dark:text-emerald-400" : "text-primary"}`}>
                                {Math.round(mem.progress)}%
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:bg-destructive/10 h-7 w-7 md:h-9 md:w-9"
                              onClick={() => setConfirmDelete(mem)}
                            >
                              <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <Dialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) { setConfirmDelete(null); setConfirmAgree(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEnglish ? "Delete Memorization" : "Hapus Hafalan"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-muted p-3">
              <p className="font-semibold">{confirmDelete?.surah_name}</p>
              {typeof confirmDelete?.memorizedCount === "number" && typeof confirmDelete?.totalAyahsCount === "number" && (
                <p className="text-sm text-muted-foreground">
                  {(isEnglish ? "Progress" : "Progres") + ": "}{confirmDelete.memorizedCount} / {confirmDelete.totalAyahsCount} {isEnglish ? "verses" : "ayat"}
                </p>
              )}
            </div>
            <p className="text-sm">
              {isEnglish ? "This action is permanent and cannot be undone." : "Tindakan ini permanen dan tidak bisa dibatalkan."}
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={confirmAgree}
                onChange={(e) => setConfirmAgree(e.target.checked)}
                className="h-4 w-4 rounded border"
              />
              {isEnglish ? "I understand and want to delete this memorization" : "Saya paham dan ingin menghapus hafalan ini"}
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              {isEnglish ? "Cancel" : "Batal"}
            </Button>
            <Button
              variant="destructive"
              disabled={!confirmAgree}
              onClick={async () => {
                if (confirmDelete?.mem_id) {
                  await deleteMemorization(confirmDelete.mem_id);
                }
                setConfirmDelete(null);
                setConfirmAgree(false);
              }}
            >
              {isEnglish ? "Delete" : "Hapus"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Hafalan;
